import React, { useEffect, useRef, useState } from "react";
import { canAnimate, clamp, useSvgPlayback } from "./motion";

// Letters arrive with momentum, lean toward the cursor, and can be grabbed and thrown.
// A thrown letter bounces off the screen edges and springs home, pulling its neighbours like a rope.
export function KineticTitle({ text, ...props }) {
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    if (!canAnimate()) {
      el.classList.add("is-settled");
      return undefined;
    }
    const letters = [...el.children];
    const bodies = letters.map(() => ({ x: 0, y: 0, vx: 0, vy: 0, r: 0, vr: 0, tx: 0, ty: 0 }));
    let raf = 0;
    let drag = null;
    let ready = false;
    const pointer = { x: 0, y: 0, on: false };
    const home = (i) => {
      const rect = letters[i].getBoundingClientRect();
      return { x: rect.left + rect.width / 2 - bodies[i].x, y: rect.top + rect.height / 2 - bodies[i].y, w: rect.width, h: rect.height };
    };
    let homes = [];
    const step = () => {
      let moving = !!drag;
      const vw = window.innerWidth;
      const vh = window.innerHeight;
      bodies.forEach((b, i) => {
        if (drag?.i === i) {
          b.vx = (drag.x - b.x) * 0.5;
          b.vy = (drag.y - b.y) * 0.5;
          b.x += b.vx;
          b.y += b.vy;
        } else {
          let tx = 0;
          let ty = 0;
          if (drag) {
            const f = [0, 0.42, 0.17, 0.06][Math.abs(i - drag.i)] || 0;
            tx = bodies[drag.i].x * f;
            ty = bodies[drag.i].y * f;
          } else if (pointer.on && homes[i]) {
            const dx = pointer.x - homes[i].x;
            const dy = pointer.y - homes[i].y;
            const d = Math.hypot(dx, dy);
            if (d < 260) {
              const f = (1 - d / 260) * 0.12;
              tx = clamp(dx * f, -16, 16);
              ty = clamp(dy * f, -12, 12);
            }
          }
          b.vx = (b.vx + (tx - b.x) * 0.022) * 0.935;
          b.vy = (b.vy + (ty - b.y) * 0.022) * 0.935;
          b.x += b.vx;
          b.y += b.vy;
          const h = homes[i];
          if (h) {
            const left = h.x - h.w / 2 + b.x;
            const right = h.x + h.w / 2 + b.x;
            const top = h.y - h.h / 2 + b.y;
            const bottom = h.y + h.h / 2 + b.y;
            if (left < 0 && b.vx < 0) (b.x -= left), (b.vx *= -0.62);
            if (right > vw && b.vx > 0) (b.x -= right - vw), (b.vx *= -0.62);
            if (top < 72 && b.vy < 0) (b.y += 72 - top), (b.vy *= -0.62);
            if (bottom > vh && b.vy > 0) (b.y -= bottom - vh), (b.vy *= -0.62);
          }
        }
        const lean = clamp(b.vx * 1.6, -38, 38);
        b.vr = (b.vr + (lean - b.r) * 0.18) * 0.72;
        b.r += b.vr;
        if (Math.abs(b.x) + Math.abs(b.y) + Math.abs(b.vx) + Math.abs(b.vy) + Math.abs(b.r) > 0.06) moving = true;
        letters[i].style.transform = `translate(${b.x.toFixed(2)}px, ${b.y.toFixed(2)}px) rotate(${b.r.toFixed(2)}deg)`;
      });
      raf = moving || pointer.on ? requestAnimationFrame(step) : 0;
      if (!raf) letters.forEach((letter) => (letter.style.transform = ""));
    };
    const wake = () => {
      if (!raf) raf = requestAnimationFrame(step);
    };
    const measure = () => {
      homes = letters.map((_, i) => home(i));
    };
    const settle = window.setTimeout(() => {
      el.classList.add("is-settled");
      ready = true;
      measure();
    }, 240 + text.length * 60 + 1100);
    const onDown = (e) => {
      const i = letters.indexOf(e.target.closest?.(".bm-kinetic > span"));
      if (!ready || i < 0 || (e.pointerType === "mouse" && e.button !== 0)) return;
      measure();
      letters[i].setPointerCapture?.(e.pointerId);
      drag = { i, id: e.pointerId, ox: e.clientX - bodies[i].x, oy: e.clientY - bodies[i].y, x: bodies[i].x, y: bodies[i].y, moved: 0 };
      el.classList.add("is-dragging");
      wake();
    };
    const onMove = (e) => {
      pointer.x = e.clientX;
      pointer.y = e.clientY;
      if (drag && e.pointerId === drag.id) {
        const x = e.clientX - drag.ox;
        const y = e.clientY - drag.oy;
        drag.moved += Math.hypot(x - drag.x, y - drag.y);
        drag.x = x;
        drag.y = y;
      }
      if (ready) wake();
    };
    const onUp = (e) => {
      if (!drag || e.pointerId !== drag.id) return;
      const b = bodies[drag.i];
      b.vx = clamp(b.vx * 1.35, -70, 70);
      b.vy = clamp(b.vy * 1.35, -70, 70);
      drag = null;
      el.classList.remove("is-dragging");
      wake();
    };
    const hero = el.closest(".brand-hero") || el;
    const onEnter = (e) => {
      if (e.pointerType !== "mouse") return;
      pointer.on = true;
      measure();
    };
    const onLeave = () => {
      pointer.on = false;
    };
    el.addEventListener("pointerdown", onDown);
    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", onUp);
    window.addEventListener("pointercancel", onUp);
    window.addEventListener("resize", measure);
    hero.addEventListener("pointerenter", onEnter);
    hero.addEventListener("pointerleave", onLeave);
    return () => {
      window.clearTimeout(settle);
      cancelAnimationFrame(raf);
      el.removeEventListener("pointerdown", onDown);
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onUp);
      window.removeEventListener("pointercancel", onUp);
      window.removeEventListener("resize", measure);
      hero.removeEventListener("pointerenter", onEnter);
      hero.removeEventListener("pointerleave", onLeave);
    };
  }, [text]);
  return (
    <h1 ref={ref} className="bm-kinetic" aria-label={text} {...props}>
      {text.split("").map((letter, i) => (
        <span key={i} aria-hidden="true" style={{ "--i": i }}>
          {letter}
        </span>
      ))}
    </h1>
  );
}

export function Scribble() {
  return (
    <svg className="bm-scribble" viewBox="0 0 200 22" preserveAspectRatio="none" aria-hidden="true">
      <path pathLength="1" d="M3 13C42 6 92 5 141 8s47 5 56 1" />
      <path pathLength="1" d="M18 18c46-4 101-5 158-2" />
    </svg>
  );
}

// The footer wordmark as a few thousand dots: they assemble on arrival, scatter from the cursor,
// burst on click and always find their way home.
export function FooterSignature({ text }) {
  const ref = useRef(null);
  const canvasRef = useRef(null);
  useEffect(() => {
    const span = ref.current;
    const host = span?.parentNode;
    const canvas = canvasRef.current;
    if (!span || !host || !canvas || !canAnimate()) return undefined;
    const ctx = canvas.getContext?.("2d");
    if (!ctx) return undefined;
    let dots = [];
    let w = 0;
    let h = 0;
    let size = 2;
    let raf = 0;
    let visible = false;
    let assembled = false;
    let cancelled = false;
    const pointer = { x: -999, y: -999, on: false };
    const build = () => {
      const box = host.getBoundingClientRect();
      const glyphs = span.getBoundingClientRect();
      if (!box.width || !glyphs.width) return;
      w = box.width;
      h = box.height;
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      canvas.width = Math.round(w * dpr);
      canvas.height = Math.round(h * dpr);
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      const style = getComputedStyle(span);
      const fontSize = parseFloat(style.fontSize) || 160;
      const gap = fontSize > 110 ? 4 : 3;
      size = gap * 0.62;
      const off = document.createElement("canvas");
      off.width = Math.ceil(w);
      off.height = Math.ceil(h);
      const o = off.getContext("2d");
      o.font = `${style.fontWeight} ${style.fontSize} ${style.fontFamily}`;
      o.textBaseline = "alphabetic";
      const m = o.measureText(text);
      const x0 = glyphs.left - box.left;
      const y0 = glyphs.top - box.top + (glyphs.height + m.actualBoundingBoxAscent - m.actualBoundingBoxDescent) / 2;
      o.fillText(text, x0, y0);
      const data = o.getImageData(0, 0, off.width, off.height).data;
      const next = [];
      for (let y = 0; y < off.height; y += gap)
        for (let x = 0; x < off.width; x += gap)
          if (data[(y * off.width + x) * 4 + 3] > 140)
            next.push({
              hx: x,
              hy: y,
              x: assembled ? x : x - 120 - Math.random() * w * 0.5,
              y: assembled ? y : y + (Math.random() - 0.5) * h * 1.6,
              vx: 0,
              vy: 0,
              delay: assembled ? 0 : (x / w) * 50 + Math.random() * 18,
            });
      dots = next;
      span.classList.add("is-particles");
    };
    const frame = (t) => {
      raf = visible ? requestAnimationFrame(frame) : 0;
      ctx.clearRect(0, 0, w, h);
      const R = w < 600 ? 60 : 95;
      const scan = ((t % 6500) / 6500) * (w + 400) - 200;
      const rest = new Path2D();
      const lit = new Path2D();
      for (const d of dots) {
        if (d.delay > 0) {
          d.delay -= 1;
          continue;
        }
        if (pointer.on) {
          const dx = d.x - pointer.x;
          const dy = d.y - pointer.y;
          const dist = Math.hypot(dx, dy) || 1;
          if (dist < R) {
            const f = (1 - dist / R) ** 2 * 5.5;
            d.vx += (dx / dist) * f - (dy / dist) * f * 0.35;
            d.vy += (dy / dist) * f + (dx / dist) * f * 0.35;
          }
        }
        const sx = Math.abs(d.hx - scan);
        if (sx < 26) d.vy -= (1 - sx / 26) * 0.35;
        d.vx = (d.vx + (d.hx - d.x) * 0.055) * 0.83;
        d.vy = (d.vy + (d.hy - d.y) * 0.055) * 0.83;
        d.x += d.vx;
        d.y += d.vy;
        const off = Math.abs(d.x - d.hx) + Math.abs(d.y - d.hy);
        (off > 2.5 ? lit : rest).rect(d.x, d.y, size, size);
      }
      ctx.fillStyle = "#cdcdcd";
      ctx.fill(rest);
      ctx.fillStyle = "#191919";
      ctx.fill(lit);
    };
    const local = (e) => {
      const rect = canvas.getBoundingClientRect();
      return [e.clientX - rect.left, e.clientY - rect.top];
    };
    const onMove = (e) => {
      [pointer.x, pointer.y] = local(e);
      pointer.on = true;
    };
    const onLeave = () => {
      pointer.on = false;
    };
    const onBurst = (e) => {
      const [px, py] = local(e);
      for (const d of dots) {
        const dx = d.x - px;
        const dy = d.y - py;
        const dist = Math.hypot(dx, dy) || 1;
        if (dist < 260) {
          const f = (1 - dist / 260) * 26 * (0.6 + Math.random() * 0.8);
          d.vx += (dx / dist) * f;
          d.vy += (dy / dist) * f;
        }
      }
    };
    const observer = window.IntersectionObserver
      ? new IntersectionObserver(([entry]) => {
          visible = entry.isIntersecting;
          if (visible && !dots.length) build();
          if (visible) assembled = true;
          if (visible && !raf) raf = requestAnimationFrame(frame);
        })
      : null;
    const start = () => {
      if (cancelled) return;
      observer?.observe(host);
    };
    if (document.fonts?.ready) document.fonts.ready.then(start);
    else start();
    const onResize = () => {
      if (Math.abs(host.getBoundingClientRect().width - w) > 2) build();
    };
    host.addEventListener("pointermove", onMove);
    host.addEventListener("pointerleave", onLeave);
    host.addEventListener("pointerdown", onBurst);
    window.addEventListener("resize", onResize);
    return () => {
      cancelled = true;
      cancelAnimationFrame(raf);
      observer?.disconnect();
      span.classList.remove("is-particles");
      host.removeEventListener("pointermove", onMove);
      host.removeEventListener("pointerleave", onLeave);
      host.removeEventListener("pointerdown", onBurst);
      window.removeEventListener("resize", onResize);
    };
  }, [text]);
  return (
    <>
      <canvas ref={canvasRef} className="bm-signature-dots" aria-hidden="true" />
      <span ref={ref} className="bm-signature">
        {text}
      </span>
    </>
  );
}

// The page as a mission route: each section is a waypoint, the download is the destination.
export function MissionRoute({ stops }) {
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    if (!el || !canAnimate()) return undefined;
    const fill = el.querySelector(".bm-route-fill");
    const marker = el.querySelector(".bm-route-marker");
    const dots = [...el.querySelectorAll(".bm-route-stop")];
    let points = [];
    let frame = 0;
    const measure = () => {
      const span = document.documentElement.scrollHeight - window.innerHeight || 1;
      points = stops.map((selector) => {
        const node = document.querySelector(selector);
        if (!node) return 1;
        const top = node.getBoundingClientRect().top + window.scrollY;
        return clamp((top - window.innerHeight * 0.45) / span);
      });
      dots.forEach((dot, i) => dot.style.setProperty("--at", points[i]));
    };
    const update = () => {
      frame = 0;
      const span = document.documentElement.scrollHeight - window.innerHeight || 1;
      const p = clamp(window.scrollY / span);
      fill.style.transform = `scaleY(${p})`;
      marker.style.setProperty("--at", p);
      dots.forEach((dot, i) => dot.classList.toggle("is-passed", p >= points[i] - 0.002));
      el.classList.toggle("is-arrived", p > 0.985);
      el.classList.toggle("is-on", window.scrollY > 120);
    };
    const onScroll = () => {
      if (!frame) frame = requestAnimationFrame(update);
    };
    const onResize = () => {
      measure();
      onScroll();
    };
    measure();
    update();
    const late = window.setTimeout(onResize, 1200);
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onResize);
    return () => {
      cancelAnimationFrame(frame);
      window.clearTimeout(late);
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onResize);
    };
  }, [stops]);
  return (
    <div ref={ref} className="bm-route" aria-hidden="true">
      <span className="bm-route-line" />
      <span className="bm-route-fill" />
      {stops.map((stop, i) => (
        <span
          key={stop}
          className={"bm-route-stop" + (i === stops.length - 1 ? " is-destination" : "")}
        />
      ))}
      <span className="bm-route-marker">
        <svg viewBox="-10 -10 20 20" width="14" height="14">
          <path d="M9 0-7-7-3 0-7 7z" fill="#191919" transform="rotate(90)" />
        </svg>
      </span>
    </div>
  );
}

// A route drawn from the headline to the download button, with Momentum travelling it.
export function DownloadRoute({ hostRef }) {
  const svg = useRef(null);
  const inView = useSvgPlayback(svg);
  const [geometry, setGeometry] = useState(null);
  useEffect(() => {
    const host = hostRef.current;
    if (!host) return undefined;
    const measure = () => {
      const box = host.getBoundingClientRect();
      const button = host.querySelector(".brand-download-control .brand-button");
      if (!box.width || !button) return;
      const b = button.getBoundingClientRect();
      const w = box.width;
      const h = box.height;
      const ex = b.left - box.left - 26;
      const ey = b.top - box.top + b.height / 2;
      setGeometry({
        w,
        h,
        ex,
        ey,
        d: `M-20 ${h * 0.86}C${w * 0.16} ${h * 0.86} ${w * 0.26} ${h * 0.97} ${w * 0.4} ${h * 0.86}S${ex - 170} ${ey - 120} ${ex} ${ey}`,
      });
    };
    measure();
    const resizer = window.ResizeObserver ? new ResizeObserver(measure) : null;
    resizer?.observe(host);
    return () => resizer?.disconnect();
  }, [hostRef]);
  return (
    <svg
      ref={svg}
      className={"bm-download-route" + (inView ? " is-in" : "")}
      viewBox={geometry ? `0 0 ${geometry.w} ${geometry.h}` : "0 0 1 1"}
      aria-hidden="true"
      focusable="false"
    >
      {geometry && (
        <>
          <defs>
            <mask id="bm-download-mask" maskUnits="userSpaceOnUse">
              <path className="bm-download-draw" d={geometry.d} pathLength="1" />
            </mask>
          </defs>
          <path className="bm-download-path" d={geometry.d} mask="url(#bm-download-mask)" />
          <g className="bm-download-destination" transform={`translate(${geometry.ex + 12} ${geometry.ey})`}>
            <circle r="4" />
            <circle r="4" className="bm-download-ping" />
          </g>
          <g className="bm-download-traveller" opacity="0">
            <path d="M9 0-7-7-3 0-7 7z" />
            <animateMotion
              dur="7s"
              begin="1.4s"
              repeatCount="indefinite"
              rotate="auto"
              path={geometry.d}
              keyPoints="0;1;1"
              keyTimes="0;.82;1"
              calcMode="spline"
              keySplines=".45 0 .25 1;0 0 1 1"
            />
            <animate attributeName="opacity" values="0;1;1;0" keyTimes="0;.06;.9;1" dur="7s" begin="1.4s" repeatCount="indefinite" />
          </g>
        </>
      )}
    </svg>
  );
}
